/**
 * The widget renders inside a shadow root, so this stylesheet is fully scoped:
 * nothing here leaks into the merchant's admin and none of their CSS reaches
 * in. Token names and values come straight from the design document.
 */

export const DARK_TOKENS = {
  '--sc-bg': '#0A0C10',
  '--sc-panel': '#0F1219',
  '--sc-panel2': '#141821',
  '--sc-elev': '#1A1F2A',
  '--sc-line': '#1E2430',
  '--sc-line2': '#2A3140',
  '--sc-tx': '#E8EBF2',
  '--sc-tx2': '#A6AFC0',
  '--sc-tx3': '#6D7787',
  '--sc-danger': '#FF4D5E',
  '--sc-danger-soft': '#2A1219',
  '--sc-ok': '#31C48D',
  '--sc-warn': '#F5B546',
  '--sc-shadow': '0 18px 50px rgba(10,12,16,.32)',
  '--sc-shadow-launcher': '0 8px 24px rgba(10,12,16,.35)',
};

export const LIGHT_TOKENS = {
  '--sc-bg': '#F4F5F8',
  '--sc-panel': '#FFFFFF',
  '--sc-panel2': '#FAFBFC',
  '--sc-elev': '#FFFFFF',
  '--sc-line': '#E4E7EC',
  '--sc-line2': '#D3D8E0',
  '--sc-tx': '#141821',
  '--sc-tx2': '#5A6472',
  '--sc-tx3': '#8A93A3',
  '--sc-danger': '#E0334A',
  '--sc-danger-soft': '#FDECEE',
  '--sc-ok': '#12855F',
  '--sc-warn': '#B87506',
  '--sc-shadow': '0 18px 50px rgba(20,24,33,.16)',
  '--sc-shadow-launcher': '0 8px 24px rgba(20,24,33,.22)',
};

const block = (tokens) =>
  Object.entries(tokens)
    .map(([k, v]) => `${k}:${v};`)
    .join('');

export const CSS = `
:host {
  all: initial;
  position: fixed;
  z-index: 2147483000;
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  ${block(DARK_TOKENS)}
}
:host([data-theme="light"]) { ${block(LIGHT_TOKENS)} }
:host([data-theme="dark"])  { ${block(DARK_TOKENS)} }
@media (prefers-color-scheme: light) {
  :host([data-theme="auto"]) { ${block(LIGHT_TOKENS)} }
}

*, *::before, *::after { box-sizing: border-box; }
button { font: inherit; color: inherit; background: none; border: none; padding: 0; cursor: pointer; }
textarea, input {
  font: inherit; color: var(--sc-tx); background: transparent;
  border: none; outline: none; resize: none; width: 100%;
}
::placeholder { color: var(--sc-tx3); }

.root {
  display: flex; flex-direction: column; align-items: flex-end; gap: 14px;
}
/**
 * Which side the panel hangs off.
 *
 * Driven by the class on the host element — superchat--left /
 * superchat--right — so one class governs both the corner the widget sits in
 * (a document-level rule, see index.jsx) and which way the panel opens. The
 * host can therefore be flipped from its own CSS or JS with no rebuild.
 */
:host(.superchat--left) .root { align-items: flex-start; }
:host(.superchat--right) .root { align-items: flex-end; }

/* ------------------------------------------------------------- launcher */

.launcher { position: relative; width: 58px; height: 58px; flex: none; }
.launcher__button {
  width: 58px; height: 58px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  background: var(--sc-accent);
  box-shadow: var(--sc-shadow-launcher);
  transition: transform .16s ease, box-shadow .16s ease;
}
.launcher__button:hover { transform: translateY(-1px) scale(1.03); }
.launcher__button:active { transform: scale(.97); }
.launcher__button:focus-visible { outline: 3px solid var(--sc-accent); outline-offset: 3px; }

.launcher__badge {
  position: absolute; top: -3px; right: -3px;
  min-width: 22px; height: 22px; padding: 0 6px; border-radius: 11px;
  background: #FF3B4E; color: #fff;
  font-size: 12px; font-weight: 700; font-variant-numeric: tabular-nums;
  display: flex; align-items: center; justify-content: center;
  /* Outlined in the host background so it reads on any admin theme. */
  border: 2.5px solid var(--sc-bg);
}
/* ---------------------------------------------------------------- panel */

.panel {
  width: 352px; max-width: calc(100vw - 32px);
  /**
   * The panel is anchored to the launcher at the bottom, so anything it cannot
   * fit grows upward and off the top of the screen — the header is the first
   * thing to go. It had no height ceiling at all, which held only as long as
   * the sum of its parts happened to fit; the email capture card was enough to
   * break that. The thread absorbs the difference (see .thread below).
   *
   * Reserves the launcher, both offsets and a little breathing room. dvh
   * where supported, so a mobile browser's collapsing toolbar does not clip it.
   */
  max-height: calc(100vh - 128px);
  max-height: calc(100dvh - 128px);
  border-radius: 14px; overflow: hidden;
  background: var(--sc-panel);
  border: 1px solid var(--sc-line);
  box-shadow: var(--sc-shadow);
  display: flex; flex-direction: column;
  animation: sc-rise .18s cubic-bezier(.2,.8,.3,1);
}
@keyframes sc-rise {
  from { opacity: 0; transform: translateY(10px) scale(.985); }
  to   { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .panel { animation: none; }
}

.header {
  padding: 13px 14px; display: flex; align-items: center; gap: 10px;
  background: var(--sc-header-bg); border-bottom: 1px solid var(--sc-header-line);
}
.header__mark {
  width: 30px; height: 30px; border-radius: 8px; flex: none;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; color: #fff; background: var(--sc-mark-bg);
}
.header__text { flex: 1; min-width: 0; }
.header__brand {
  font-size: 13px; font-weight: 650; letter-spacing: -.01em; color: var(--sc-header-tx);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.header__status {
  font-size: 11px; color: var(--sc-header-tx2);
  display: flex; align-items: center; gap: 5px; margin-top: 1px;
}
.header__dot { width: 6px; height: 6px; border-radius: 50%; flex: none; }
.header__action {
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 6px; flex: none;
  color: var(--sc-header-tx2);
}
.header__action:hover { background: rgba(127,127,127,.14); }
.header__action:focus-visible { outline: 2px solid var(--sc-accent); outline-offset: 1px; }

/* -------------------------------------------------------- email capture */

/* Sits above the thread, styled as a quiet aside rather than a modal — it is
   an offer, and a visitor who ignores it should feel no friction at all. */
.emailcapture {
  /* Never the thing that shrinks — it is a fixed offer above a scrolling
     thread, and a squashed one reads as a rendering fault. */
  flex: none;
  margin: 10px 12px 0; padding: 11px 12px;
  border: 1px solid var(--sc-line); border-radius: 10px;
  background: var(--sc-panel2);
  display: flex; flex-direction: column; gap: 7px;
}
.emailcapture__head { display: flex; align-items: center; gap: 8px; }
.emailcapture__title {
  flex: 1; font-size: 12.5px; font-weight: 650; color: var(--sc-tx);
}
.emailcapture__dismiss {
  flex: none; display: flex; padding: 2px; border-radius: 5px; color: var(--sc-tx3);
}
.emailcapture__dismiss:hover { background: rgba(127,127,127,.14); }
.emailcapture__sub { font-size: 11.5px; color: var(--sc-tx2); line-height: 1.45; }
.emailcapture__row { display: flex; gap: 7px; align-items: stretch; }
/* Its own class rather than .field__input: that one styles a read-only
   <div> and carries display: flex with no width, which on a real <input>
   collapses to the intrinsic size. It also sits on --sc-panel2, the same
   colour as this card, so the field disappeared into it. */
.emailcapture__input {
  flex: 1; min-width: 0; width: 100%;
  height: 34px; border-radius: 8px; border: 1px solid var(--sc-line);
  background: var(--sc-panel); padding: 0 10px;
  font-size: 12.5px; color: var(--sc-tx);
}
.emailcapture__input::placeholder { color: var(--sc-tx3); }
.emailcapture__input:focus { border-color: var(--sc-accent); outline: none; }
/* width, not just flex: the base .button is width: 100%, which a
   flex: none does nothing about — it took the whole row and left the input
   with no space at all. */
.emailcapture__row .button {
  width: auto; flex: none; height: 34px; padding: 0 14px; font-size: 12px;
}
.emailcapture__error { font-size: 11px; color: var(--sc-danger, #FF3B4E); }

/* The collapsed form, after a dismissal. One line, no border, easy to skip. */
.emailcapture__reopen {
  margin: 8px 12px 0; padding: 5px 8px; align-self: flex-start;
  font-size: 11.5px; color: var(--sc-tx2); border-radius: 7px;
}
.emailcapture__reopen:hover { background: rgba(127,127,127,.12); color: var(--sc-tx); }

/* ------------------------------------------------------------- pre-chat */

.form { padding: 18px 16px; display: flex; flex-direction: column; gap: 14px; }
.form__lede { font-size: 16px; font-weight: 650; color: var(--sc-tx); letter-spacing: -.015em; }
.form__sub { font-size: 12.5px; color: var(--sc-tx2); line-height: 1.5; margin-top: 4px; }
.form__fields { display: flex; flex-direction: column; gap: 9px; }
.field__label {
  font-size: 10.5px; font-weight: 650; letter-spacing: .06em;
  color: var(--sc-tx3); margin-bottom: 5px;
}
.field__input {
  height: 34px; border-radius: 8px; border: 1px solid var(--sc-line);
  background: var(--sc-panel2); display: flex; align-items: center;
  padding: 0 10px; font-size: 12.5px; color: var(--sc-tx);
}
.field__input--readonly { color: var(--sc-tx2); }
.field__textarea {
  min-height: 66px; border-radius: 8px; border: 1px solid var(--sc-line);
  background: var(--sc-panel2); padding: 9px 10px; font-size: 12.5px;
  line-height: 1.5; color: var(--sc-tx);
}
.field__textarea--tall { min-height: 78px; }
.field__input:focus-within, .field__textarea:focus-within,
.field__textarea:focus { border-color: var(--sc-accent); }

.topics { display: flex; gap: 6px; flex-wrap: wrap; }
.topic {
  padding: 6px 10px; border-radius: 7px; font-size: 12px;
  border: 1px solid var(--sc-line); color: var(--sc-tx2); background: var(--sc-panel2);
}
.topic[aria-pressed="true"] {
  color: #fff; border-color: transparent; background: var(--sc-accent);
}

.button {
  height: 38px; border-radius: 9px; width: 100%;
  display: flex; align-items: center; justify-content: center; gap: 7px;
  font-size: 13px; font-weight: 650; color: #fff; background: var(--sc-accent);
  transition: filter .15s ease;
}
.button:hover:not(:disabled) { filter: brightness(1.08); }
.button:disabled { opacity: .55; cursor: not-allowed; }
.button:focus-visible { outline: 2px solid var(--sc-accent); outline-offset: 2px; }

.notice {
  padding: 10px 12px; border-radius: 9px; background: var(--sc-panel2);
  border: 1px solid var(--sc-line); display: flex; gap: 9px; align-items: flex-start;
}
.notice__title { font-size: 12.5px; font-weight: 600; color: var(--sc-tx); }
.notice__body { font-size: 11.5px; color: var(--sc-tx2); line-height: 1.5; margin-top: 2px; }
.notice--error { border-color: var(--sc-danger); background: var(--sc-danger-soft); }
.notice--error .notice__title { color: var(--sc-danger); }

/* ---------------------------------------------------------- help centre */

.docs { display: flex; flex-direction: column; gap: 6px; }
.docs__label {
  font-size: 10.5px; font-weight: 650; letter-spacing: .06em; color: var(--sc-tx3);
}
.doc {
  display: flex; align-items: center; gap: 8px; text-align: left;
  padding: 8px 10px; border-radius: 8px;
  border: 1px solid var(--sc-line); background: var(--sc-panel2);
}
.doc:hover { border-color: var(--sc-accent); }
.doc__title {
  flex: 1; min-width: 0; font-size: 12px; color: var(--sc-tx);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.doc-body {
  padding: 16px; font-size: 12.5px; line-height: 1.65; color: var(--sc-tx2);
  max-height: 340px; overflow-y: auto; white-space: pre-wrap;
}
.doc-body h3 { font-size: 14px; color: var(--sc-tx); margin: 0 0 8px; }

/* --------------------------------------------------------------- thread */

.thread {
  padding: 14px; display: flex; flex-direction: column; gap: 10px;
  background: var(--sc-panel2);
  /**
   * 250px is what it wants, not a floor it must keep. As a flex-basis the
   * empty thread still stands 250px tall as before, but min-height: 0 lets
   * it give ground when the panel hits its ceiling — so a short window costs
   * message space rather than the header.
   */
  flex: 1 1 250px; min-height: 0; max-height: 380px;
  overflow-y: auto; overscroll-behavior: contain;
}
.thread::-webkit-scrollbar { width: 8px; }
.thread::-webkit-scrollbar-thumb { background: var(--sc-line2); border-radius: 4px; }

.thread__day {
  text-align: center; font-size: 10.5px; color: var(--sc-tx3); letter-spacing: .04em;
}
.thread__more {
  align-self: center; font-size: 11px; font-weight: 600; color: var(--sc-accent);
  padding: 4px 10px; border-radius: 6px;
}

.bubble {
  max-width: 80%; padding: 9px 11px; font-size: 12.5px; line-height: 1.5;
  word-break: break-word; white-space: pre-wrap;
}
.bubble--in {
  align-self: flex-start; border-radius: 12px 12px 12px 4px;
  background: var(--sc-elev); border: 1px solid var(--sc-line); color: var(--sc-tx);
}
.bubble--out {
  align-self: flex-end; border-radius: 12px 12px 4px 12px;
  background: var(--sc-accent); color: #fff;
}
.bubble--system {
  align-self: center; max-width: 90%; text-align: center; padding: 6px 10px;
  font-size: 11.5px; color: var(--sc-tx3); background: transparent;
}
/* A withdrawn message keeps the bubble's shape and side — so the thread still
   reads as a conversation — but loses its colour, because there is nothing
   left in it to read. */
.bubble--deleted {
  background: transparent; border: 1px dashed var(--sc-line2);
  color: var(--sc-tx3); font-style: italic;
}
.bubble__meta {
  font-size: 10px; color: var(--sc-tx3); padding: 0 3px;
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
}
.bubble-wrap { display: flex; flex-direction: column; gap: 3px; max-width: 88%; }
.bubble-wrap--out { align-self: flex-end; align-items: flex-end; }
.bubble-wrap--in { align-self: flex-start; align-items: flex-start; }

.attachment { margin-top: 6px; }
.attachment__thumb {
  display: block; width: 100%; margin-bottom: 4px;
  border-radius: 8px; overflow: hidden; background: var(--sc-elev);
}
.attachment__thumb img { display: block; width: 100%; max-height: 200px; object-fit: contain; }
.attachment__row {
  display: flex; align-items: center; gap: 6px; width: 100%;
  font-size: 11.5px; color: inherit; text-align: left;
}
.attachment__name { text-decoration: underline; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attachment__size { flex: none; opacity: .65; font-size: 10px; }
.attachment--expired {
  display: flex; align-items: center; gap: 6px;
  font-size: 11.5px; opacity: .6;
}
.attachment--expired .attachment__name { text-decoration: line-through; }

.typing {
  align-self: flex-start; display: flex; align-items: center; gap: 4px;
  padding: 9px 12px; border-radius: 12px;
  background: var(--sc-elev); border: 1px solid var(--sc-line);
}
.typing span {
  width: 5px; height: 5px; border-radius: 50%; background: var(--sc-tx3);
  animation: sc-blink 1.2s infinite;
}
.typing span:nth-child(2) { animation-delay: .18s; }
.typing span:nth-child(3) { animation-delay: .36s; }
@keyframes sc-blink { 0%, 60%, 100% { opacity: .28; } 30% { opacity: 1; } }

/* ------------------------------------------------------------- composer */

.composer {
  padding: 10px 12px; border-top: 1px solid var(--sc-line);
  display: flex; align-items: flex-end; gap: 9px; background: var(--sc-panel);
}
.composer__input {
  flex: 1; font-size: 12.5px; line-height: 1.5; max-height: 96px;
  padding: 4px 0; color: var(--sc-tx);
}
.composer__send {
  width: 28px; height: 28px; border-radius: 7px; flex: none;
  display: flex; align-items: center; justify-content: center;
  background: var(--sc-accent); transition: filter .15s ease;
}
.composer__send:disabled { opacity: .45; cursor: not-allowed; }
.composer__send:hover:not(:disabled) { filter: brightness(1.1); }
.composer__icon { color: var(--sc-tx3); flex: none; display: flex; }
.composer__icon:hover { color: var(--sc-tx2); }

/* Picked files sit above the input, so a long filename never squeezes the
   textarea down to nothing. */
.composer__files {
  display: flex; flex-wrap: wrap; gap: 5px;
  padding: 8px 12px 0; background: var(--sc-panel);
}
.chip {
  display: flex; align-items: center; gap: 5px; max-width: 100%;
  padding: 3px 5px 3px 8px; border-radius: 6px;
  border: 1px solid var(--sc-line); background: var(--sc-elev);
  font-size: 11px; color: var(--sc-tx2);
}
.chip--ready { color: var(--sc-tx); }
.chip--failed { border-color: var(--sc-danger); color: var(--sc-danger); }
.chip__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chip__meta { flex: none; font-size: 10px; opacity: .7; }
.chip__remove { display: flex; color: var(--sc-tx3); }
.chip__remove:hover { color: var(--sc-danger); }

/* --------------------------------------------------------------- footer */

.footer {
  padding: 7px 12px; border-top: 1px solid var(--sc-line); background: var(--sc-panel);
  display: flex; align-items: center; justify-content: center; gap: 5px;
  font-size: 10px; color: var(--sc-tx3); letter-spacing: .02em;
}
.footer a { color: inherit; text-decoration: none; }

.diagnostic-bar {
  padding: 8px 14px;
  background: var(--sc-accent);
  color: #fff;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.4;
}

/* ---------------------------------------------------------- diagnostics */

.testtools {
  padding: 9px 12px;
  border-top: 1px dashed var(--sc-line2);
  background: var(--sc-panel);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.testtools__label {
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.1em;
  color: var(--sc-tx3);
}
.testtools__row { display: flex; gap: 5px; flex-wrap: wrap; }
.testtools__button {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid var(--sc-line);
  background: var(--sc-panel2);
  color: var(--sc-tx2);
  font-size: 10.5px;
  font-weight: 600;
}
.testtools__button:hover:not(:disabled) { border-color: var(--sc-accent); color: var(--sc-tx); }
.testtools__button:disabled { opacity: 0.45; cursor: not-allowed; }
.testtools__button--danger { color: var(--sc-danger); border-color: var(--sc-danger); }
.testtools__button--primary {
  color: #fff;
  background: var(--sc-accent);
  border-color: transparent;
}
.testtools__button--primary:hover:not(:disabled) { filter: brightness(1.08); color: #fff; }
.testtools__note {
  font-size: 10px;
  line-height: 1.5;
  color: var(--sc-tx3);
}
.testtools__note code {
  font-family: 'IBM Plex Mono', ui-monospace, monospace;
  font-size: 9.5px;
}

.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

/**
 * On a phone the panel stops being a card floating over the merchant's admin
 * and becomes the screen. A 352px card inset from a 390px viewport leaves the
 * thread about 44 characters wide with the keyboard up, which is the width at
 * which a support conversation stops being readable.
 *
 * The host element is fixed and carries no transform, so a fixed child inside
 * this shadow root lays out against the viewport — the panel can break out of
 * the launcher's corner without the host having to know anything about it.
 */
@media (max-width: 560px) {
  .panel {
    position: fixed;
    inset: 0;
    width: auto;
    max-width: none;
    max-height: none;
    border: none;
    border-radius: 0;
    /* Notches and the home indicator: the header and the composer are the two
       things that would otherwise sit under them. */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }
  /* Full height now, so the ceiling that kept it inside a card would only
     leave dead space under the last message. */
  .thread { flex: 1 1 auto; max-height: none; }
  .doc-body { max-height: none; flex: 1 1 auto; }

  .header { padding: 14px; }
  .header__action { width: 32px; height: 32px; }

  /* Below 16px iOS Safari zooms the whole admin in on focus, and it does not
     zoom back out when the field is left. */
  .composer__input,
  .field__input,
  .field__textarea,
  .emailcapture__input { font-size: 16px; }
  .emailcapture__input,
  .field__input { height: 40px; }
  .composer { padding: 10px 12px 12px; }
  .composer__send { width: 34px; height: 34px; }
  .topic { padding: 8px 12px; }
  .doc { padding: 10px 12px; }
  .bubble { max-width: 88%; font-size: 13.5px; }
}

/* Landscape on a phone: the sheet is still right, but a 380px-tall viewport
   cannot spare the footer or a tall pre-chat form. */
@media (max-width: 900px) and (max-height: 440px) {
  .footer { display: none; }
  .form { padding: 12px 14px; gap: 10px; }
  .field__textarea { min-height: 48px; }
}
`;

export default CSS;
